import { Box, Flex, Spinner } from "@chakra-ui/react";
import { SendSvg } from "@root/assets/icons";
import { PauseButton } from "@root/components/common/PauseButton";
import { StyledTextareaContainer } from "@root/components/dashboard/style";
import { useMainChatContext } from "@root/context/kps-chat-context";
import { useTranslation } from "react-i18next";
import { useRef, useEffect } from "react";
import { useRiskAssessmentContext } from "@root/context/risk-assessment-context";
import { useDispatch } from "react-redux";
import {
  updateFileIndex,
  updateFileIndexType,
} from "@root/store/slices/entities";
import { isPdfUrlValidThunk } from "@root/store/thunks";
import { validateUrl } from "@root/utils/helpers";
import { WEB_PROJECTS, PDF } from "@root/webConstants";
import { CHAT_STATUS } from "@root/constants";

export const LegalComplianceTextArea = ({
  onChangehandler,
  input,
  onSend,
  fileIndex,
  noOfDocs,
  loading,
  name = "",
  pid = WEB_PROJECTS.KSCE_GPT.id,
  stopPolling,
  currentStatus,
}: any) => {
  const {
    isMessaging,
    urlType,
    setFlagValidateUrl,
    fetchPdfPreview,
    setPdfLinkLoading,
  } = useMainChatContext();

  const inputRef: any = useRef(null);
  const dispatch = useDispatch();
  const { t } = useTranslation("common");

  // AI Model selection state (shared via hook) - using AskAI pattern
  const { selectedAskAiModel } = useRiskAssessmentContext();

  // AI Model dropdown state (commented out like AskAI)
  // const [isAIModelDropdownOpen, setIsAIModelDropdownOpen] = useState(false);
  // const dropdownRef = useRef<HTMLDivElement>(null);

  // const handleAIModelSelect = (option: { id: number; labelKey: string }) => {
  //   setSelectedAskAiModel(option);
  //   setIsAIModelDropdownOpen(false);
  // };

  const isSendDisabled = input?.trim?.()?.length === 0;
  const isButtonDisabled = isSendDisabled || Boolean(loading);

  useEffect(() => {
    if (inputRef?.current) {
      inputRef.current.style.height = "42px";
      inputRef.current.style.height = `${inputRef?.current?.scrollHeight}px`;
    }
  }, [inputRef?.current?.scrollHeight]);

  const onChangeHandler = (e: any) => {
    if (inputRef?.current) {
      inputRef.current.style.height = "42px";
      inputRef.current.style.height = `${inputRef?.current?.scrollHeight}px`;
    }
    setFlagValidateUrl?.(true);
    onChangehandler?.(e);
  };

  const getPlaceholderText = () => {
    return t("legalCompliance.input.placeholder");
  };

  const onKeyPressHandler = async (e: any) => {
    switch (e.keyCode) {
      case 40:
        if (fileIndex < noOfDocs) {
          dispatch(updateFileIndex(fileIndex + 1));
          dispatch(updateFileIndexType("inc"));
        }
        break;
      case 38:
        if (fileIndex > 1) {
          dispatch(updateFileIndex(fileIndex - 1));
          dispatch(updateFileIndexType("dec"));
        }
        break;
      case 13:
        if (!e?.shiftKey) {
          e.preventDefault();
          let validate = false;
          const url = inputRef?.current?.value;
          if (urlType?.value === PDF) {
            try {
              setPdfLinkLoading?.(true);
              const res: any = await dispatch(
                isPdfUrlValidThunk({ payload: { url } })
              );
              validate = res?.payload?.data;
              fetchPdfPreview?.({ url });
            } catch (err) {
              validate = false;
            } finally {
              setPdfLinkLoading?.(false);
            }
          } else {
            validate = Boolean(validateUrl?.(urlType?.value, url));
          }
          setFlagValidateUrl?.(validate);
          if (validate) onSubmit?.();
        }
        break;
    }
  };

  const onSubmit = () => {
    if (isButtonDisabled) {
      return;
    }
    const input = inputRef?.current?.value;
    onSend?.(input, selectedAskAiModel?.id);
    return;
  };

  const chatButton = () => {
    if (!loading)
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          cursor={isButtonDisabled ? "not-allowed" : "pointer"}
          onClick={isButtonDisabled ? () => {} : onSubmit}
          width="40px"
          height="40px"
          borderRadius="50%"
          bg="#005EB8"
          opacity={isButtonDisabled ? "0.5" : "1"}
          _hover={{
            bg: "blue.600",
          }}
          transition="all 0.2s"
        >
          <SendSvg />
        </Box>
      );
    else if (loading && currentStatus === CHAT_STATUS?.STREAMING)
      return <PauseButton onClick={() => stopPolling?.()} />;
    else
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="40px"
          height="40px"
          borderRadius="50%"
          bg="#005EB8"
          opacity={isButtonDisabled ? "0.5" : "1"}
          _hover={{
            bg: "blue.600",
          }}
          transition="all 0.2s"
        >
          <Spinner color="white" />
        </Box>
      );
  };

  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      position={"relative"}
      className="DashBoardTextarea"
      bg="white"
      border="1px solid #CBD5E1"
      borderRadius="16px"
      cursor="text"
      // Clicking anywhere in the box (except its buttons) focuses the text field
      onClick={(e: any) => {
        if (!(e.target as HTMLElement)?.closest?.("button")) inputRef?.current?.focus();
      }}
      boxShadow="0 1px 2px rgba(15, 23, 42, 0.06), 0 6px 16px rgba(15, 23, 42, 0.05)"
      transition="border-color 0.15s, box-shadow 0.15s"
      _focusWithin={{
        borderColor: "#005EB8",
        boxShadow: "0 0 0 3px rgba(0, 94, 184, 0.12)",
      }}
    >
      <Box w={"100%"} h={"100%"} borderRadius={"16px"} bg={"white"}>
        <Flex
          position={"relative"}
          width={"100%"}
          alignItems="center"
          gap="12px"
          px="16px"
          py="8px"
        >
          {/* Text Input */}
          <StyledTextareaContainer
            ref={inputRef}
            onChange={onChangeHandler}
            border={0}
            outline={0}
            value={input}
            onKeyDown={
              loading ? () => {} : isMessaging ? () => {} : onKeyPressHandler
            }
            name={name}
            placeholder={getPlaceholderText()}
            flex="1"
            maxH={"100%"}
            minH="24px"
            overflowY={"auto"}
            fontSize="16px"
            lineHeight="1.5"
            _placeholder={{
              color: "#9CA3AF",
              fontSize: "16px",
            }}
          />

          {/* Attachment Icon */}
        </Flex>
        <Flex
          px={"16px"}
          justifyContent={"space-between"}
          alignItems={"center"}
          w={"100%"}
          pt="10px"
          pb="16px"
        >
          <Flex alignItems={"center"}>
            {/* AI Model Selection Dropdown - commented out like AskAI */}
            {/* <Box position="relative" ref={aiModelDropdownRef}>
              <Box
                p="1px"
                borderRadius="60px"
                bgGradient="linear(180deg, #005EB8 0%, #B15906 100%)"
                cursor="pointer"
                onClick={handleAIModelDropdownToggle}
                _hover={{
                  bgGradient: "linear(180deg, #005EB8 0%, #B15906 100%)",
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  gap="8px"
                  px="12px"
                  py="8px"
                  h="40px"
                  borderRadius="60px"
                  bg="white"
                  _hover={{ bg: "#F9FAFB" }}
                >
                  <Box display="flex" alignItems="center" gap="6px">
                    <Image src={PromptIcon} />
                    <Text fontSize="14px" color="#010610" fontWeight="500">
                      {selectedAskAiModel?.labelKey ? 
                        t(selectedAskAiModel.labelKey) :
                        t("legalCompliance.models.selectModels")}
                    </Text>
                  </Box>
                  <Box width="1px" height="16px" bg="#E5E7EB" />
                  <Box
                    width="12px"
                    height="12px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <ChevronDownIcon />
                  </Box>
                </Box>
              </Box>
              {isAIModelDropdownOpen && (
                <Box
                  position="absolute"
                  {...(dropdownPosition === "top"
                    ? { bottom: "100%", mb: "4px" }
                    : { top: "100%", mt: "4px" })}
                  left="0"
                  right="0"
                  bg="white"
                  w="280px"
                  borderRadius="8px"
                  border="1px solid #E5E7EB"
                  boxShadow="0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)"
                  zIndex={1000}
                >
                  {AI_MODEL_OPTIONS?.map((option, index) => (
                    <Box
                      key={option.id}
                      px="12px"
                      py="8px"
                      cursor="pointer"
                      bg={
                        selectedAskAiModel?.id === option?.id
                          ? "#F3F4F6"
                          : "white"
                      }
                      _hover={{
                        bg:
                          selectedAskAiModel?.id === option?.id
                            ? "#F3F4F6"
                            : "#F9FAFB",
                      }}
                      onClick={() => handleAIModelSelect?.(option)}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Text fontSize="14px" color="#374151">
                        {option?.labelKey ? t(option.labelKey) : (option as any)?.label}
                      </Text>
                      {selectedAskAiModel?.id === option?.id && (
                        <Box
                          width="16px"
                          height="16px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Box
                            width="12px"
                            height="12px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Text
                              fontSize="12px"
                              color="#374151"
                              fontWeight="bold"
                            >
                              ✓
                            </Text>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box> */}
          </Flex>
          {/*  Button */}
          {chatButton()}
        </Flex>
      </Box>
    </Flex>
  );
};
